export type AdminEnumValue = number | string | boolean | null | undefined;
export type AdminEnumLabels = Record<number, string>;

export const JOB_APPLICATION_STATUS_LABELS: AdminEnumLabels = {
  0: "Submitted",
  1: "Viewed",
  2: "Resume Opened",
  3: "Contacts Revealed",
  4: "Removed",
  5: "Shortlisted",
  6: "Declined"
};

export const INTERVIEW_STATUS_LABELS: AdminEnumLabels = {
  0: "Not Viewed",
  1: "Viewed",
  2: "Interview Scheduled",
  3: "Missed Interview"
};

export const JOB_STATUS_LABELS: AdminEnumLabels = {
  1: "Posted",
  2: "Has Offers",
  3: "Booked",
  4: "Completed",
  5: "Offers Expired",
  6: "Expired",
  7: "Cancelled"
};

export const JOB_TYPE_LABELS: AdminEnumLabels = {
  0: "Hygienist",
  1: "Assistant",
  2: "Admin",
  3: "Associate Dentist"
};

export const JOB_STATUS_COLORS: Record<number, string> = {
  1: "#2196F3",
  2: "#FF9800",
  3: "#4CAF50",
  4: "#2E7D32",
  5: "#9E9E9E",
  6: "#F44336",
  7: "#424242"
};

export const HIRE_JOB_STATUS_LABELS: AdminEnumLabels = {
  0: "Pending Review",
  1: "Approved",
  2: "Removed",
  3: "Expired"
};

export const OFFER_STATUS_LABELS: AdminEnumLabels = {
  0: "Sent",
  1: "Rejected",
  2: "Accepted",
  3: "Expired",
  4: "Removed"
};

export const OFFER_STATUS_COLORS: Record<number, string> = {
  0: "#0B4598",
  1: "#FB7672",
  2: "#13CA82",
  3: "#FFA726",
  4: "#9E9E9E"
};

export const USER_ACCOUNT_STATUS_LABELS: AdminEnumLabels = {
  [-4]: "Deleted",
  1: "Active"
};

export const REMINDER_STATUS_LABELS: AdminEnumLabels = {
  0: "Pending",
  1: "Reminder Sent"
};

export const BOOLEAN_LABELS: AdminEnumLabels = {
  0: "No",
  1: "Yes"
};

export const INVOICE_PAID_STATUS_LABELS: AdminEnumLabels = {
  0: "Unpaid",
  1: "Paid",
  2: "Manually / Already Paid Offline",
  3: "Paid by Central DSO"
};

export const CENTRAL_BOOKING_LABELS: AdminEnumLabels = {
  0: "Regular Office",
  1: "DentalCorp",
  2: "123D"
};

const numericValue = (value: AdminEnumValue): number | null => {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function formatAdminEnum(
  labels: AdminEnumLabels,
  value: AdminEnumValue,
  emptyLabel = "—"
): string {
  if (value === null || value === undefined || value === "") return emptyLabel;
  const numeric = numericValue(value);
  if (
    numeric !== null &&
    Object.prototype.hasOwnProperty.call(labels, numeric)
  ) {
    return labels[numeric];
  }
  return String(value);
}

type KnownTableEnum = {
  tableNames: string[];
  column: string;
  labels: AdminEnumLabels;
};

const KNOWN_TABLE_ENUMS: KnownTableEnum[] = [
  { tableNames: ["job", "jobs"], column: "status", labels: JOB_STATUS_LABELS },
  { tableNames: ["job", "jobs"], column: "type", labels: JOB_TYPE_LABELS },
  {
    tableNames: ["jobapplications"],
    column: "status",
    labels: JOB_APPLICATION_STATUS_LABELS
  },
  {
    tableNames: ["jobapplications"],
    column: "interviewStatus",
    labels: INTERVIEW_STATUS_LABELS
  },
  {
    tableNames: ["hirejob", "jobposts"],
    column: "status",
    labels: HIRE_JOB_STATUS_LABELS
  },
  {
    tableNames: ["partialoffer"],
    column: "status",
    labels: OFFER_STATUS_LABELS
  },
  {
    tableNames: ["shift", "shifts"],
    column: "remindStatus",
    labels: REMINDER_STATUS_LABELS
  },
  {
    tableNames: ["hygienist"],
    column: "accountStatus",
    labels: USER_ACCOUNT_STATUS_LABELS
  },
  {
    tableNames: ["user"],
    column: "status",
    labels: USER_ACCOUNT_STATUS_LABELS
  },
  {
    tableNames: ["user"],
    column: "accountStatus",
    labels: USER_ACCOUNT_STATUS_LABELS
  },
  { tableNames: ["invoice"], column: "sent", labels: BOOLEAN_LABELS },
  { tableNames: ["invoice"], column: "creditPaid", labels: BOOLEAN_LABELS },
  {
    tableNames: ["invoice"],
    column: "dentistMarkedPaid",
    labels: INVOICE_PAID_STATUS_LABELS
  },
  {
    tableNames: ["invoice"],
    column: "hygienistMarkedPaid",
    labels: INVOICE_PAID_STATUS_LABELS
  },
  {
    tableNames: ["location"],
    column: "isCentralBooked",
    labels: CENTRAL_BOOKING_LABELS
  }
];

export function formatKnownTableEnum(
  tableName: string | null | undefined,
  column: string,
  value: AdminEnumValue
): string | undefined {
  const normalizedTable = String(tableName || "").toLowerCase();
  const match = KNOWN_TABLE_ENUMS.find(
    entry =>
      entry.column === column && entry.tableNames.includes(normalizedTable)
  );
  return match ? formatAdminEnum(match.labels, value) : undefined;
}

export const formatJobStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(JOB_STATUS_LABELS, value);

export const formatJobType = (value: AdminEnumValue): string =>
  formatAdminEnum(JOB_TYPE_LABELS, value);

export const formatOfferStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(OFFER_STATUS_LABELS, value);

export const formatJobApplicationStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(JOB_APPLICATION_STATUS_LABELS, value);

export const formatInterviewStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(INTERVIEW_STATUS_LABELS, value);

export const formatHireJobStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(HIRE_JOB_STATUS_LABELS, value);

export const formatReminderStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(REMINDER_STATUS_LABELS, value);

export const formatInvoicePaidStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(INVOICE_PAID_STATUS_LABELS, value);

export const formatUserAccountStatus = (value: AdminEnumValue): string =>
  formatAdminEnum(USER_ACCOUNT_STATUS_LABELS, value, "Unknown");

export const formatCentralBooking = (value: AdminEnumValue): string =>
  formatAdminEnum(CENTRAL_BOOKING_LABELS, value, "Regular Office");
